import React, { FC, MouseEventHandler, useMemo, useState } from 'react';
import { CatalogDetail } from '../../apis/detail-catalog';
import { Container } from '../../components/container/container';
// @ts-ignore
import renderHTML from 'react-render-html';
import striptags from 'striptags';
import {
  Breadcrumbs,
  Button,
  PageHeader,
} from '@plataforma-xvia/design-system-es';
import { LocationSection } from './components/location-section';
import { ArrowIcon } from './components/icons';
import {
  HowToRegOutlined,
  PersonOutlined,
  AccountBalanceOutlined,
  CastOutlined,
  CorporateFareOutlined,
} from '@mui/icons-material';
import { CatalogRating } from '../../components/rating/catalog-rating';
import { TabSection, type TabKey } from './components/tab-section';
import { SuspensionBanner } from './components/suspension-banner';
import styles from './styles.module.css';
import { ROUTES } from '../../routes';
import { buildAppPath } from '../../utils/basePath';

export interface CatalogProps extends CatalogDetail {
  onPdfClick?: MouseEventHandler;
  onFavoriteClick?: MouseEventHandler;
  onServiceClick?: MouseEventHandler;
  breadcrumbs?: Array<{
    name?: string;
    path?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
  }>;
}

export const Catalog: FC<CatalogProps> = ({
  name,
  link,
  isFree,
  isOnline,
  updated_at,
  onPdfClick,
  onFavoriteClick,
  onServiceClick,
  description,
  info,
  steps,
  targets,
  department,
  category,
  departmentSections,
  averageRatings,
  id,
  slug,
  suspension,
}) => {
  type TabKey = 'steps' | 'info' | 'sections';

  const [selectedCity, setSelectedCity] = useState<string>();
  const [selectedSection, setSelectedSection] = useState<string>();
  const [activeTab, setActiveTab] = useState<TabKey>('steps');

  const handleCityChange = (value: string) => {
    setSelectedCity(value);
    setSelectedSection(undefined);
  };

  const handleSectionChange = (value: string) => {
    setSelectedSection(value);
  };


  const isSuspended = useMemo(() => {
    if (!suspension?.enabled) return false;
    const today = new Date().toISOString().split('T')[0];
    if (suspension.startDate > today) return false;
    if (suspension.endDate && suspension.endDate < today) return false;
    return true;
  }, [suspension]);

  const canRequest = useMemo(
    () => Boolean(isOnline && link),
    [isOnline, link]
  );

  const detailItems = useMemo(() => {
    const items: Array<{ icon: React.ElementType; text: string }> = [];

    if (targets && targets.length > 0) {
      const normalize = (value: string) =>
        value
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .trim()
          .toLowerCase();

      const dedupedByKey = new Map<string, string>();
      targets.forEach((target) => {
        const key = normalize(target);
        if (!key) return;
        const existing = dedupedByKey.get(key);
        if (!existing || (target !== key && existing === key)) {
          dedupedByKey.set(key, target);
        }
      });

      const dedupedTargets = Array.from(dedupedByKey.values());
      items.push({ icon: PersonOutlined, text: dedupedTargets.join(', ') });
    }

    if (department) {
      items.push({
        icon: AccountBalanceOutlined,
        text: department.shortName || department.name,
      });
    }

    items.push({
      icon: isOnline ? CastOutlined : CorporateFareOutlined,
      text: isOnline ? 'Serviço Online' : 'Atendimento presencial',
    });

    return items;
  }, [department, isOnline, targets]);

  const tabs: Array<{ id: TabKey; label: string }> = useMemo(
    () => [
      { id: 'steps', label: 'Etapas' },
      { id: 'info', label: 'Orientações' },
      { id: 'sections', label: 'Unidades' },
    ],
    []
  );

  return (
    <div className={styles.catalog}>
      <PageHeader
        title={name}
        breadcrumb={
          <Breadcrumbs
            tone="inverted"
            showBackButton
            items={[
              { name: 'Início', path: buildAppPath(ROUTES.HOME_PAGE) },
              { name: 'Carta de Serviços', path: buildAppPath(ROUTES.SERVICE_LIST_PAGE) },
              { name },
            ]}
          />
        }
        action={
          canRequest ? (
            <div className={styles.requestButtonWrapper}>
              <Button
                variant="solid"
                tone="primary"
                size="lg"
                label="Solicitar"
                iconEnd={<ArrowIcon />}
                onClick={onServiceClick}
                disabled={isSuspended}
              />
            </div>
          ) : undefined
        }
      />

      <Container>
        {isSuspended && suspension && (
          <div className={styles.suspensionBannerWrapper}>
            <SuspensionBanner
              message={suspension.message}
              endDate={suspension.endDate}
            />
          </div>
        )}

        <div className={styles.mainContent}>
          <div className={styles.contentWrapper}>
            <div className={styles.summarySection}>
              {category?.name && (
                <span className={styles.categoryTag}>{category.name}</span>
              )}
              {department?.description && (
                <div className={styles.summaryDescription}>
                  {renderHTML(department.description)}
                </div>
              )}
              {description && (
                <p className={styles.description}>{striptags(description)}</p>
              )}
            </div>

            <div className={styles.tabSection}>
              <TabSection
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                steps={steps}
                info={info}
                departmentSections={departmentSections}
              />
            </div>

            {id && (
              <div className={styles.ratingWrapper}>
                <CatalogRating
                  serviceId={id}
                  serviceName={name}
                  averageRatings={averageRatings}
                />
              </div>
            )}
          </div>

          <div className={styles.sideContent}>
            {departmentSections && departmentSections.length > 0 && (
              <LocationSection
                departmentSections={departmentSections}
                selectedCity={selectedCity}
                selectedSection={selectedSection}
                onCityChange={handleCityChange}
                onSectionChange={handleSectionChange}
              />
            )}

            <div className={styles.detailsCard}>
              <div className={styles.detailsTitle}>
                <HowToRegOutlined className={styles.detailsTitleIcon} />
                Detalhes do serviço
              </div>
              <div className={styles.detailsList}>
                {detailItems.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <div className={styles.detailsItem} key={`details-${index}`}>
                      <span className={styles.detailsIcon}>
                        <IconComponent fontSize="small" />
                      </span>
                      <div className={styles.detailsText}>{item.text}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};
